/**
 * Bates stamping configured once for a merged document and applied to every
 * member (VE-30105).
 *
 * The settings live in the `project-redaction-data` BODY, beside the set's
 * watermarks — not on the project TDO's `details`, where they began. This
 * header said otherwise long after the move, which is worth correcting
 * rather than deleting: `parseBatesSettings` below is exported precisely so
 * the two readers of that body cannot disagree about what a valid stamp is,
 * and a header pointing at the old home sends the next reader to the wrong
 * one.
 *
 * NOTHING IS WRITTEN TO MEMBER TDOs. A member does not need a marker saying it
 * belongs to a project: `projectOfFolder(requestFolder)` already finds the
 * project from the request, and `project.members` gives both membership and
 * index. The member asks; it stores nothing.
 *
 * PRECEDENCE: for a member of a project that has these settings, the project's
 * win and the member's own `session.batesStamp` is ignored. Non-members are
 * unaffected. One rule, so the two can never both apply.
 */
import {
  clampBatesSettings,
  MAX_BATES_START_NUMBER,
} from '@redact-modules/documents/batesLabel';
import type { BatesStampSettings } from '@redact-modules/documents/models';
import type { TDOId } from '@common-modules/universal/models/Brands';
import { readFilePageCount } from '@redact-modules/casemedia/pageCount';

/**
 * What `details.batesStamp` holds.
 *
 * `version` is a monotonic counter bumped on every settings change, NOT a
 * timestamp: a timestamp answers "when" rather than "which", and equal or
 * skewed ones make poor identity. A member's burned output records the version
 * it was stamped under, so "the stamp itself changed" is one comparison and
 * "the numbers moved" is another — see the plan.
 */
export interface ProjectBatesStamp {
  /**
   * When these settings last changed — the identity an output records.
   * Compared for EQUALITY only; see `projectRedactionAsset`'s header for why
   * a stamp rather than a counter or an asset id.
   */
  readonly modifiedDateTime: string | null;
  /** The shared settings. `startNumber` is the FIRST number of the whole set. */
  readonly settings: BatesStampSettings;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Coerce a raw settings object into clamped `BatesStampSettings`, or null.
 *
 * EXPORTED and shared, because the project's settings now live in the
 * `project-redaction-data` body rather than the TDO's details and both
 * readers must agree on what a valid stamp is. Two transcriptions of this
 * rule is how the project and the editor once gave opposite answers for the
 * same document.
 *
 * `details` is hand-editable server JSON, and so is the asset body, so this
 * is a trust boundary: clamping beats rejecting, because a slightly-off
 * stamp an operator can see and correct is better than silently losing the
 * sequence on a document that already had one.
 */
export const parseBatesSettings = (raw: unknown): BatesStampSettings | null => {
  if (!isRecord(raw)) {
    return null;
  }
  const s = raw as Partial<BatesStampSettings>;
  // The three numbers the stamp cannot be drawn without. Anything else is
  // defaulted by the clamp.
  if (
    typeof s.digits !== 'number' ||
    typeof s.startNumber !== 'number' ||
    typeof s.fontSize !== 'number'
  ) {
    return null;
  }
  const anchorH =
    s.anchorH === 'left' || s.anchorH === 'right' || s.anchorH === 'center'
      ? s.anchorH
      : 'right';
  return clampBatesSettings({
    digits: s.digits,
    startNumber: s.startNumber,
    prefix: s.prefix,
    suffix: s.suffix,
    anchorH,
    anchorV: s.anchorV === 'top' ? 'top' : 'bottom',
    fontSize: s.fontSize,
    color: s.color,
    bold: s.bold,
    insetXPt: s.insetXPt,
    insetYPt: s.insetYPt,
  });
};

/**
 * The settings as a flat record with FIXED keys, for comparing.
 *
 * Typed `Record<keyof BatesStampSettings, unknown>` so a field added to the
 * settings is a compile error here rather than a field silently left out of
 * every equality test — which is the failure mode worth guarding, since the
 * cost of missing one is an edit nothing notices.
 */
const comparableBatesSettings = (
  settings: BatesStampSettings
): Record<keyof BatesStampSettings, unknown> => {
  const clamped = clampBatesSettings(settings);
  return {
    digits: clamped.digits,
    startNumber: clamped.startNumber,
    prefix: clamped.prefix,
    suffix: clamped.suffix,
    anchorH: clamped.anchorH,
    anchorV: clamped.anchorV,
    fontSize: clamped.fontSize,
    color: clamped.color,
    bold: clamped.bold,
    insetXPt: clamped.insetXPt,
    insetYPt: clamped.insetYPt,
  };
};

/**
 * Are two settings the same stamp?
 *
 * CLAMPED ON BOTH SIDES before comparing, because callers disagree about
 * whether they hold clamped values: the panel hands over whatever was typed,
 * while anything read back from a write has already been through the bounds.
 * A 25-character prefix compared against its own stored 20-character form is
 * the same stamp, and without clamping here it reads as a change.
 *
 * FIELD BY FIELD, and NOT by serialising the objects.
 *
 * It was `JSON.stringify(clampBatesSettings(a)) === JSON.stringify(...b)`,
 * which compares key ORDER as well as value. `clampBatesSettings` returns
 * `{...input, ...}`, so the order it produces is its input's — and the two
 * sides are built by different code: settings read back from the stored body
 * come through `parseBatesSettings`'s literal, while a panel opened before
 * that record loaded seeds its draft from `defaultBatesStampSettings()`,
 * whose keys run in another order. Re-entering the stored values then
 * compared UNEQUAL, minted a fresh stamp, and left every member of the set
 * reading behind — offering a rebuild of the whole release that would
 * produce byte-identical PDFs, which is the one thing this guard exists to
 * prevent.
 *
 * Every field is a primitive, so `===` is the whole comparison. Absent and
 * `undefined` are the same stamp, which is what the clamp already returns for
 * an empty affix.
 *
 * Shared rather than written at each call site: the reducer uses it to no-op
 * an unchanged commit and the saga uses it to skip the write that would
 * follow, and a rule transcribed twice is one that drifts.
 */
export const isSameBatesSettings = (
  a: BatesStampSettings | null | undefined,
  b: BatesStampSettings | null | undefined
): boolean => {
  if (!a || !b) {
    // Both absent is the same stamp; one absent is not. `null` and
    // `undefined` both mean "no settings", so they compare equal to each
    // other as well.
    return !a && !b;
  }
  const left = comparableBatesSettings(a);
  const right = comparableBatesSettings(b);
  return (Object.keys(left) as (keyof BatesStampSettings)[]).every(
    (key) => left[key] === right[key]
  );
};

/** As much of a request's file as a page count can be read from. */
export interface BatesStartFile {
  readonly id?: unknown;
  readonly details?: unknown;
}

/** One member, as the arithmetic below needs it. */
export interface BatesSpanMember {
  readonly tdoId: TDOId;
  /**
   * The member's ORIGINAL page count, or null when it has not loaded.
   *
   * Original, never the redacted output's `numPages`. Deleted pages keep their
   * numbers (VE-30106): a 10-page document with page 3 deleted draws
   * `start+0, +1, +3 … +9`, so it CONSUMES ten numbers while producing nine
   * pages. Using the output count would start the next member on a number the
   * previous one has already drawn.
   */
  readonly originalPageCount: number | null;
}

/**
 * The first Bates number of each member, in merge order.
 *
 * Null once a page count is missing — and for every member AFTER it, because a
 * start is the sum of everything before it and an unknown span makes the rest
 * unknowable too. That is the honest answer rather than a guess, and it is
 * what lets the caller report "cannot be checked" instead of a wrong number.
 */
export const effectiveBatesStarts = (
  members: readonly BatesSpanMember[],
  startNumber: number
): ReadonlyMap<string, number | null> => {
  const out = new Map<string, number | null>();
  let cursor: number | null = Number.isSafeInteger(startNumber)
    ? startNumber
    : null;
  for (const member of members) {
    out.set(String(member.tdoId), cursor);
    if (cursor === null) {
      continue;
    }
    const span = member.originalPageCount;
    if (span === null || !Number.isSafeInteger(span) || span < 0) {
      cursor = null;
      continue;
    }
    const next = cursor + span;
    // Past the ceiling the sequence stops incrementing in float arithmetic and
    // every page takes the SAME number — the one failure an identifier must
    // never have. `batesLabel` holds the same bound for the single-file case.
    cursor = next > MAX_BATES_START_NUMBER ? null : next;
  }
  return out;
};

/**
 * The same map, built from the LOADED FILES of a request.
 *
 * `effectiveBatesStarts` needs a page count per member, and the two surfaces
 * that judge a release — the Documents panel's row badges and the editor's
 * merged banner — both have the files themselves rather than the project's
 * cached spans. They were not both using it: the editor passed no map at all,
 * so a member whose numbering had moved answered `unknown` and the banner
 * reported a merged document stale with no reason under it. That is the one
 * thing a shared rule prevents, so it is shared rather than copied.
 *
 * UNDEFINED IS NOT AN EMPTY MAP. With no record or no membership read, every
 * member would look like it expects no number, which reads as orphaned for
 * the whole release while the lookup is outstanding.
 *
 * Takes the member ids IN MERGE ORDER — the order is the input, since what a
 * reorder changes is exactly this.
 */
export const batesStartsFromFiles = (args: {
  readonly files: readonly (BatesStartFile | null | undefined)[];
  readonly memberIds: readonly (TDOId | string)[] | null | undefined;
  readonly record: ProjectBatesStamp | null | undefined;
}): ReadonlyMap<string, number | null> | undefined => {
  const { files, memberIds, record } = args;
  if (!record || !memberIds) {
    return undefined;
  }
  const pageCountById = new Map(
    files
      .filter((file): file is BatesStartFile => !!file)
      .map((file) => [String(file.id), readFilePageCount(file.details)])
  );
  return effectiveBatesStarts(
    memberIds.map((tdoId) => ({
      tdoId: tdoId as TDOId,
      originalPageCount: pageCountById.get(String(tdoId)) ?? null,
    })),
    record.settings.startNumber
  );
};

/** The first Bates number for one member, or null when it cannot be known. */
export const effectiveBatesStartFor = (
  members: readonly BatesSpanMember[],
  startNumber: number,
  tdoId: TDOId | string | undefined
): number | null =>
  tdoId === undefined
    ? null
    : (effectiveBatesStarts(members, startNumber).get(String(tdoId)) ?? null);

/** The merged document's stamp as it applies to ONE member. */
export interface GoverningBates {
  /** The set's settings, with `startNumber` replaced by this member's start. */
  readonly settings: BatesStampSettings;
  /** The record this came from, recorded on anything burned with it. */
  readonly projectModifiedDateTime: string | null;
  /** This member's first number — recorded too, since a reorder moves it. */
  readonly startNumber: number;
  /**
   * The page count the sequence ALLOCATED to this member, from
   * `details.veritoneFile.pageCount`.
   *
   * Reported so the burn can check it against the PDF it is about to stamp.
   * If the FILE HAS MORE PAGES than this, the next member's start lands
   * inside this one's drawn range and the release carries duplicate
   * identifiers with nothing to detect it. Only that direction is a defect —
   * fewer pages leaves a gap, which this feature already ships by design
   * (VE-30106). See `batesSpanOverrun`.
   *
   * WRITTEN BY THIS REPO ON THE FAST-PDF PATH, and by the converter engine
   * otherwise. `setupPdfFastUpload` reads the uploaded PDF with pdf.js and
   * `serviceFinalizePdfTdo` writes the count onto `veritoneFile`, so for a
   * fast-path upload it is measured from the same bytes the burn later reads
   * and cannot drift. A converter-ingested file is where it can.
   */
  readonly assumedPageCount: number | null;
}

/**
 * Would stamping this file draw PAST the span the sequence allocated it?
 *
 * The burn numbers from `startNumber`, so the highest number it draws is
 * `start + burnPageCount - 1` while the allocation runs to
 * `start + assumedPageCount - 1`. An overrun puts this member's tail numbers
 * on the next member's head numbers, and nothing downstream can see it: the
 * staleness check re-runs the same arithmetic and agrees with itself. The
 * caller burns WITHOUT numbering when this is true — no number beats a wrong
 * one on a released page.
 *
 * ONLY THE OVERRUN DIRECTION (VE-31894). This was an equality check, which
 * burned a file unnumbered in two states that cannot collide:
 *
 *  - FEWER real pages than allocated leaves a gap before the next member, not
 *    a collision — and gaps are already this feature's accepted outcome, since
 *    VE-30106 has deleted pages keep their numbers and every export with one
 *    ships a gap by design. Treating it as fatal contradicted that.
 *  - A NULL `assumedPageCount` has nothing downstream to collide with.
 *    `effectiveBatesStarts` records a member's own start BEFORE it consumes
 *    that member's span, then nulls the cursor — so this member keeps a valid
 *    start and every member after it is ungoverned and draws nothing.
 *
 * Equality was also stricter than the data warrants: `assumedPageCount` is
 * written by this repo on the fast-PDF upload path, measured from the same
 * bytes the burn later reads, so it cannot drift there at all.
 *
 * A NULL `burnPageCount` DOES count. There the allocation is known, so later
 * members do have starts, and a file we cannot measure has an unbounded drawn
 * range — the one case where failing open could actually collide.
 */
export const batesSpanOverrun = (args: {
  readonly assumedPageCount: number | null;
  readonly burnPageCount: number | null;
}): boolean =>
  args.burnPageCount === null ||
  (args.assumedPageCount !== null &&
    args.burnPageCount > args.assumedPageCount);

/**
 * Does a merged document's stamping govern this file, and with what?
 *
 * NULL UNLESS IT GENUINELY GOVERNS — the record exists, the membership is
 * KNOWN, this file is in it, and its start is computable. A caller cannot
 * recover any of those from a non-null answer, which is the point: the
 * previous shape returned the document's own stamp for "not a member" and for
 * "start not computable" alike, so every caller that treated a truthy result
 * as "the project governed" was wrong twice over. One of them wrote provenance
 * onto outputs the project had nothing to do with.
 *
 * `members === undefined` means the membership has not been READ. That is not
 * an empty set: treating it as one makes every file a non-member for as long
 * as the read is outstanding.
 */
export const resolveGoverningBates = (args: {
  readonly record: ProjectBatesStamp | null | undefined;
  readonly members: readonly TDOId[] | undefined;
  readonly pageCountOf: (tdoId: TDOId) => number | null;
  readonly tdoId: TDOId | string | undefined;
}): GoverningBates | null => {
  const { record, members, pageCountOf, tdoId } = args;
  if (!record || !members || !tdoId) {
    return null;
  }
  if (!members.some((id) => String(id) === String(tdoId))) {
    return null;
  }
  const startNumber = effectiveBatesStartFor(
    members.map((id) => ({ tdoId: id, originalPageCount: pageCountOf(id) })),
    record.settings.startNumber,
    tdoId
  );
  if (startNumber === null) {
    return null;
  }
  return {
    settings: { ...record.settings, startNumber },
    projectModifiedDateTime: record.modifiedDateTime,
    startNumber,
    assumedPageCount: pageCountOf(tdoId as TDOId),
  };
};

/**
 * Is a file's redacted output still carrying the right numbering?
 *
 * FOUR VALUES, for the same reason `combinedStaleVerdict` has three: a check
 * that cannot run must not answer "fine". `unknown` covers a membership that
 * has not been read and a start that cannot be computed yet; reporting either
 * as `orphaned` paints the whole request with warnings while a fetch is in
 * flight, and reporting them as `current` hides a real defect.
 *
 * ONE IMPLEMENTATION, called by the folder rows and by the editor. The two
 * surfaces had their own, and their orphan rules had already drifted apart.
 */
/**
 * `orphaned` and `strayNumbering` are the SAME DEFECT with different causes,
 * and they are kept apart for one reason: the sentence. Both mean "this
 * output carries numbering that belongs to nothing", and both are cleared by
 * burning the file again — but one is about a file that left the set and the
 * other is about a file still in it, whose set stopped stamping. Told as one,
 * the only copy that fits is the first, and a current member gets told it is
 * no longer in the merged document.
 */
export type BatesVerdict =
  | 'current'
  | 'behind'
  | 'orphaned'
  | 'strayNumbering'
  | 'unknown';

export const batesNumberingVerdict = (args: {
  readonly hasOutput: boolean;
  readonly membershipKnown: boolean;
  readonly isMember: boolean;
  /**
   * The set's current record version, `null` when the set carries NO
   * stamping, and `undefined` when the record has not been READ.
   *
   * Three values, not two, for the same reason `membershipKnown` is its own
   * flag: collapsing "not read" into "none" produced a confident `current`
   * for every member while a lookup was outstanding, or permanently if it
   * failed.
   */
  readonly projectModifiedDateTime: string | null | undefined;
  readonly expectedStart: number | null;
  readonly carriedModifiedDateTime: string | null;
  readonly carriedStart: number | null;
  /**
   * The burn refused to stamp this output because the span the sequence
   * allocated did not match the document. It carries no numbering AND none is
   * owed until the page count is corrected, so it is `unknown` rather than
   * `behind` — a verdict whose remedy is the button that just declined to act
   * would send the reviewer round a loop.
   */
  readonly spanMismatch?: boolean;
}): BatesVerdict => {
  // Nothing produced yet: unstamped, not behind.
  if (!args.hasOutput) {
    return 'current';
  }
  if (!args.membershipKnown || args.projectModifiedDateTime === undefined) {
    return 'unknown';
  }
  if (args.spanMismatch) {
    return 'unknown';
  }
  if (!args.isMember) {
    // Left the set — or was never in it. Only a concern if its pages still
    // carry a set's numbering, which now collides with whatever took its
    // place in the order.
    return args.carriedModifiedDateTime === null ? 'current' : 'orphaned';
  }
  if (args.projectModifiedDateTime === null) {
    // The set carries no stamping. An output that still has its numbers is
    // the same hazard as a former member's: the numbers belong to nothing,
    // and a rebuild is what removes them. Reporting `current` here said the
    // opposite of what CLEAR_PROJECT_BATES_REQUEST promises.
    //
    // A SEPARATE VERDICT from the one above, because this file IS a member.
    // Sharing `orphaned` meant sharing its banner, which opens "This file is
    // no longer in the merged document" — false, and the first thing a
    // reviewer checks is the panel, where the file is plainly still listed.
    return args.carriedModifiedDateTime === null ? 'current' : 'strayNumbering';
  }
  if (args.carriedModifiedDateTime !== args.projectModifiedDateTime) {
    return 'behind';
  }
  // The version moves when the stamp is edited; the start moves when the
  // ORDER changes and the version does not. Checking one misses the other.
  if (args.expectedStart === null) {
    return 'unknown';
  }
  return args.carriedStart === args.expectedStart ? 'current' : 'behind';
};
